import React, { useEffect, useRef, useState } from 'react';
import { GridBackground } from '../GridBackground';
import ResultsSection from './ResultsSection';
import NutritionSection from './NutritionSection';

/**
 * AngledCurtainRevealTransition
 * 
 * Transición de máscara angular vinculada al scroll (Angled curtain reveal).
 * 
 * - Estructura y superposición:
 *   Nutrición (capa inferior / z-10) permanece fija en el fondo.
 *   Resultados (capa superior / z-20) actúa como telón cubriendo la pantalla.
 * 
 * - Corte en diagonal:
 *   Borde geométrico inclinado (inclinación ascendente hacia la derecha: yLeft > yRight),
 *   emulando la referencia visual del telón diagonal.
 * 
 * - Comportamiento dinámico con el scroll:
 *   1. 0.00 a 0.20: Resultados permanece 100% visible, fijo e interactivo.
 *   2. 0.20 a 0.85: El corte diagonal asciende suavemente, cortando Resultados y descubriendo Nutrición.
 *   3. 0.85 a 1.00: Nutrición 100% visible e interactivo.
 *   Al invertir el scroll hacia arriba, el telón desciende cubriendo Nutrición de manera reversible.
 */
export default function AngledCurtainRevealTransition() {
  const containerRef = useRef(null);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!containerRef.current) return;

      if (!ticking) {
        window.requestAnimationFrame(() => {
          const rect = containerRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const totalDistance = rect.height - windowHeight;

          if (totalDistance <= 0) {
            ticking = false;
            return;
          }

          const currentScrolled = -rect.top;
          const rawProgress = currentScrolled / totalDistance;
          const clamped = Math.min(Math.max(rawProgress, 0), 1);
          setScrollProgress(clamped);

          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ─── Rangos de transición (Beats) ─────────────────────────────
  // 0.00 a 0.20: Resultados en reposo completo (100% visible para lectura e interacción)
  // 0.20 a 0.85: Recorrido del corte diagonal descubriendo Nutrición
  // 0.85 a 1.00: Nutrición 100% visible
  const TRANSITION_START = 0.20;
  const TRANSITION_END = 0.85;

  let t = 0;
  if (scrollProgress <= TRANSITION_START) {
    t = 0;
  } else if (scrollProgress >= TRANSITION_END) {
    t = 1;
  } else {
    t = (scrollProgress - TRANSITION_START) / (TRANSITION_END - TRANSITION_START);
  }

  // Inclinación angular (delta entre izquierda y derecha en % del alto de pantalla)
  // yLeft es mayor que yRight: la diagonal sube de izquierda a derecha.
  const delta = isMobile ? 12 : 16;

  // Centro de la línea diagonal (en % del alto de pantalla)
  // Comienza en 115% (completamente fuera por abajo) y termina en -15% (completamente fuera por arriba)
  const lineY = 115 - t * 130;
  const yLeft = Number((lineY + delta / 2).toFixed(2));
  const yRight = Number((lineY - delta / 2).toFixed(2));

  // Polígono clip-path para la capa superior (telón crema con Resultados):
  // Cubre desde la parte superior (0 0, 100% 0) hasta la línea diagonal inferior
  const clipPathStyle = `polygon(0% 0%, 100% 0%, 100% ${yRight}%, 0% ${yLeft}%)`;

  // Control de interactividad:
  // Cuando el telón se retrae en su mayoría (t > 0.80), los clics van directo a Nutrición.
  const isTopInteractive = t < 0.80;

  // Mostrar el divisor diagonal solo mientras la línea cruza el viewport
  const showDividerLine = t > 0.01 && t < 0.99;

  return (
    <section
      id="resultados"
      ref={containerRef}
      className="relative w-full z-20 border-none outline-none"
      style={{
        // Altura de 240vh: tiempo adecuado para contemplar Resultados y luego la revelación
        height: '240vh',
        backgroundColor: '#e7e1d9',
        // Compensación de 1.5px para absorber micro-costuras de subpíxel en Edge / escalados DPI
        marginTop: '-1.5px',
        transform: 'translateZ(0)',
        WebkitTransform: 'translateZ(0)',
        backfaceVisibility: 'hidden',
      }}
    >
      {/* Ancla para navegación directa a #servicioNutricion */}
      <div
        id="servicioNutricion"
        className="absolute left-0 w-full h-1 pointer-events-none"
        style={{ top: '80%' }}
      />

      {/* Contenedor Fijo / Sticky en el Viewport */}
      <div
        className="sticky top-0 w-full h-screen overflow-hidden select-none border-none outline-none"
        style={{
          backgroundColor: '#e7e1d9',
          transform: 'translateZ(0)',
          WebkitTransform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
        }}
      >

        {/* ─── CAPA INFERIOR / FONDO: NUTRICIÓN (z-10) ─────────────── */}
        <div className="absolute inset-0 w-full h-full z-10 overflow-hidden">
          <NutritionSection />
        </div>

        {/* ─── CAPA SUPERIOR / TELÓN CREMA DIAGONAL: RESULTADOS (z-20) ─────────── */}
        <div
          className="absolute inset-0 w-full h-full z-20 overflow-hidden flex flex-col justify-center items-center"
          style={{
            backgroundColor: '#e7e1d9',
            clipPath: clipPathStyle,
            WebkitClipPath: clipPathStyle,
            pointerEvents: isTopInteractive ? 'auto' : 'none',
            willChange: 'clip-path',
          }}
        >
          <GridBackground
            gridColor="rgba(100, 95, 90, 0.16)"
            mask={false}
          />
          <div className="relative z-10 w-full h-full flex flex-col justify-center items-center">
            <ResultsSection />
          </div>
        </div>

        {/* ─── LÍNEA DIVISORIA ANGULAR Y SOMBRA GEOMÉTRICA (z-30) ──── */}
        {showDividerLine && (
          <svg
            className="absolute inset-0 w-full h-full pointer-events-none z-30"
            preserveAspectRatio="none"
            viewBox="0 0 100 100"
          >
            <defs>
              <filter id="angledCutShadow" x="-20%" y="-20%" width="140%" height="140%">
                <feDropShadow dx="0" dy="6" stdDeviation="5" floodColor="#000000" floodOpacity="0.55" />
              </filter>
            </defs>

            {/* Sombra proyectada del telón sobre la capa de abajo */}
            <line
              x1="0"
              y1={yLeft}
              x2="100"
              y2={yRight}
              stroke="rgba(0,0,0,0.6)"
              strokeWidth="0.8"
              filter="url(#angledCutShadow)"
            />

            {/* Borde geométrico fino sobrio y elegante (sin neón) */}
            <line
              x1="0"
              y1={yLeft}
              x2="100"
              y2={yRight}
              stroke="rgba(0,0,0,0.18)"
              strokeWidth="0.35"
            />
          </svg>
        )}

      </div>
    </section>
  );
}
